/** /perf - perf supervisor dashboard.
 *
 *  Lists every codified inspection (see backend/src/perf-supervisor/)
 *  with its current state, rationale, last-24h sparkline, and any
 *  autoheal actions taken. Polls /api/perf/checks every 30 s.
 *
 *  Modeled on /status's styling so the operator has a single visual
 *  language across the two surfaces. /status answers "are my
 *  dependencies up?"; /perf answers "are the invariants I've codified
 *  still holding?"
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, XCircle, Circle, RefreshCw, Wrench } from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";

type CheckStatus = "pass" | "warn" | "critical" | "skipped";

interface HistoryRow {
  ts: string;
  status: CheckStatus;
  message: string;
  duration_ms: number | null;
  autoheal_action: string | null;
}

interface CheckRow {
  id: string;
  label: string;
  rationale: string;
  cadenceMs: number;
  hasAutoheal: boolean;
  current: {
    status: CheckStatus;
    message: string;
    durationMs: number | null;
    detail: Record<string, unknown> | null;
    lastRunAt: number | null;
    lastFailureAt: number | null;
    lastAutohealAt: number | null;
  };
  history: HistoryRow[];
}

interface PerfResponse {
  ok: true;
  ts: string;
  overall: CheckStatus;
  window_hours: number;
  checks: CheckRow[];
}

const POLL_INTERVAL_MS = 30_000;

function StatusIcon({ status, className = "h-5 w-5" }: { status: CheckStatus; className?: string }) {
  if (status === "pass") return <CheckCircle2 className={`${className} text-emerald-500`} />;
  if (status === "warn") return <AlertTriangle className={`${className} text-amber-500`} />;
  if (status === "critical") return <XCircle className={`${className} text-rose-500`} />;
  return <Circle className={`${className} text-slate-400`} />;
}

function statusColor(s: CheckStatus): string {
  if (s === "pass") return "rgb(16,185,129)"; // emerald-500
  if (s === "warn") return "rgb(245,158,11)"; // amber-500
  if (s === "critical") return "rgb(244,63,94)"; // rose-500
  return "rgb(148,163,184)"; // slate-400
}

function relTime(iso: string | number | null): string {
  if (iso == null) return "never";
  const ts = typeof iso === "number" ? iso : new Date(iso).getTime();
  const ms = Date.now() - ts;
  if (ms < 0) return "now";
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function Sparkline({ history }: { history: HistoryRow[] }) {
  const W = 240;
  const H = 28;
  const N = history.length;
  if (N === 0) {
    return <div className="text-xs text-slate-400">no history yet</div>;
  }
  // Render one vertical bar per sample, oldest left to newest right.
  // Bar height encodes status severity: pass = full height, warn =
  // 2/3, critical = full, skipped = 1/4. Color encodes status.
  const barWidth = Math.max(2, Math.floor(W / Math.max(N, 1)));
  return (
    <svg width={W} height={H} role="img" aria-label="last 24h history">
      {history.map((h, i) => {
        const h2 = h.status === "skipped" ? 0.25 : h.status === "warn" ? 0.66 : 1;
        const barHeight = Math.max(2, Math.round(H * h2));
        return (
          <rect
            key={i}
            x={i * barWidth}
            y={H - barHeight}
            width={Math.max(barWidth - 1, 1)}
            height={barHeight}
            fill={statusColor(h.status)}
          />
        );
      })}
    </svg>
  );
}

function CadenceLabel({ ms }: { ms: number }) {
  if (ms < 60_000) return <>{Math.round(ms / 1000)} s</>;
  if (ms < 3_600_000) return <>{Math.round(ms / 60_000)} min</>;
  return <>{Math.round(ms / 3_600_000)} h</>;
}

interface EndpointMetric {
  endpoint: string;
  total: number;
  errorRate: number;
  p50: number;
  p95: number;
  p99: number;
}

interface MetricsResponse {
  windowMs: number;
  totals: { total: number; errorRate: number; p50: number; p95: number; p99: number };
  endpoints: EndpointMetric[];
}

export function Perf() {
  const [data, setData] = useState<PerfResponse | null>(null);
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let ctl = new AbortController();
    const load = async () => {
      ctl.abort();
      ctl = new AbortController();
      try {
        const [r, m] = await Promise.all([
          fetch("/api/perf/checks?since_hours=24", {
            credentials: "same-origin",
            cache: "no-store",
            signal: ctl.signal,
          }),
          fetch("/api/bridge/metrics", {
            credentials: "same-origin",
            cache: "no-store",
            signal: ctl.signal,
          }).catch(() => null),
        ]);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as PerfResponse;
        if (cancelled) return;
        setData(body);
        setErr(null);
        if (m && m.ok) {
          try {
            const mb = (await m.json()) as MetricsResponse;
            if (!cancelled) setMetrics(mb);
          } catch { /* leave previous */ }
        }
      } catch (e) {
        if (cancelled) return;
        if ((e as Error).name === "AbortError") return;
        setErr((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const intervalId = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(intervalId);
    };
  }, []);

  if (loading && !data) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <h1 className="text-xl font-semibold">Perf supervisor</h1>
        <p className="mt-2 text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <h1 className="text-xl font-semibold">Perf supervisor</h1>
        <p className="mt-2 text-sm text-rose-500">Error: {err}</p>
      </div>
    );
  }

  if (!data) return null;

  // Recent autoheal events (last 24 h) for the operator log at the
  // bottom of the page. Flat list across all checks, newest first.
  const autohealEvents = data.checks
    .flatMap((c) =>
      c.history
        .filter((h) => h.autoheal_action)
        .map((h) => ({ check: c.label, ts: h.ts, action: h.autoheal_action!, status: h.status })),
    )
    .sort((a, b) => (a.ts < b.ts ? 1 : -1))
    .slice(0, 20);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Perf supervisor</h1>
          <p className="mt-1 text-xs text-slate-500">
            Codified inspections. Last 24 h of results.{" "}
            <Link to="/status" className="underline">
              See /status for dependency probes
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusIcon status={data.overall} />
          <span className="text-sm font-medium capitalize">{data.overall}</span>
          {err && <span className="ml-2 text-xs text-amber-500">poll error: {err}</span>}
        </div>
      </div>

      <div className="mt-4">
        <TeachingBanner id="perf_overview" />
      </div>

      <div className="mt-6 space-y-3">
        {data.checks.map((c) => (
          <div
            key={c.id}
            className="rounded-md border border-slate-200 p-3 dark:border-slate-700"
          >
            <div className="flex items-start gap-3">
              <StatusIcon status={c.current.status} className="mt-0.5 h-5 w-5" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.label}</span>
                  <span className="text-xs text-slate-400">
                    cadence <CadenceLabel ms={c.cadenceMs} />
                  </span>
                  {c.hasAutoheal && (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                      title="autoheal action wired"
                    >
                      <Wrench className="h-3 w-3" />
                      autoheal
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{c.rationale}</p>
                <p className="mt-1 text-sm">{c.current.message}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                  <Sparkline history={c.history} />
                  <div className="flex flex-col">
                    <span>last run {relTime(c.current.lastRunAt)}</span>
                    {c.current.lastFailureAt && (
                      <span>last failure {relTime(c.current.lastFailureAt)}</span>
                    )}
                    {c.current.lastAutohealAt && (
                      <span>last autoheal {relTime(c.current.lastAutohealAt)}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <h2 className="text-sm font-semibold">Recent autoheal events</h2>
        {autohealEvents.length === 0 ? (
          <p className="mt-1 text-xs text-slate-500">
            None in the last {data.window_hours} h. (autoheal triggers only on critical status.)
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {autohealEvents.map((e, i) => (
              <li key={i} className="rounded border border-slate-200 p-2 text-xs dark:border-slate-700">
                <span className="font-medium">{e.check}</span> at {relTime(e.ts)}
                {": "}
                <span className="text-slate-600 dark:text-slate-400">{e.action}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {metrics && metrics.endpoints.length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm font-semibold">
            Bridge endpoint latency (last {Math.round(metrics.windowMs / 60_000)} min)
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            p50 and p95 per endpoint. When the user reports intermittent slowness, this is the
            authoritative read on whether the bridge actually slowed down or whether something
            else (worker tick, network) was the cause.
          </p>
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-slate-500">
                <tr className="text-left">
                  <th className="py-1 pr-3 font-medium">endpoint</th>
                  <th className="py-1 pr-3 font-medium tabular-nums">calls</th>
                  <th className="py-1 pr-3 font-medium tabular-nums">p50</th>
                  <th className="py-1 pr-3 font-medium tabular-nums">p95</th>
                  <th className="py-1 pr-3 font-medium tabular-nums">p99</th>
                  <th className="py-1 pr-3 font-medium tabular-nums">err</th>
                </tr>
              </thead>
              <tbody>
                {metrics.endpoints
                  .slice()
                  .sort((a, b) => b.total - a.total)
                  .slice(0, 10)
                  .map((e) => (
                    <tr key={e.endpoint} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1 pr-3 font-mono">{e.endpoint}</td>
                      <td className="py-1 pr-3 tabular-nums">{e.total}</td>
                      <td className="py-1 pr-3 tabular-nums">{e.p50}</td>
                      <td
                        className={`py-1 pr-3 tabular-nums ${
                          e.p95 > 2000 ? "text-amber-600 dark:text-amber-400" : ""
                        }`}
                      >
                        {e.p95}
                      </td>
                      <td className="py-1 pr-3 tabular-nums">{e.p99}</td>
                      <td
                        className={`py-1 pr-3 tabular-nums ${
                          e.errorRate > 0.02 ? "text-rose-600 dark:text-rose-400" : ""
                        }`}
                      >
                        {(e.errorRate * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 text-xs text-slate-400">
        <RefreshCw className="h-3 w-3" />
        Polling every 30 s. See <code>docs/perf-supervisor.md</code> for the contract.
      </div>
    </div>
  );
}
